import { useCallback, useMemo } from 'react'

import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { TooltipContentProps } from 'recharts'
import type { Payload } from 'recharts/types/component/DefaultTooltipContent'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { ChartMarkerLabels } from '~/components/common/ChartMarkerLabels'
import { LeftAlignedYAxisTick } from '~/components/common/LeftAlignedYAxisTick'
import {
  BALANCE_WEATHER_DEW_COLOR,
  BALANCE_WEATHER_PRECIP_COLOR,
  BALANCE_WEATHER_TEMP_COLOR,
} from '~/constants/chartPalette'
import { badgeColors } from '~/models/badgeColors'
import { formatChartTick } from '~/models/chartFormatting'
import { isTargetDateTodayInJst, toProtoDate } from '~/models/dateTime'
import { komaEndTime, komaNumberToTime } from '~/models/komaToTime'
import type { WeatherActualKoma } from '~/models/weather/actualKoma'

/** どの気象系列を描画するか (分析補助/詳細トグルに対応)。 */
export type WeatherSeriesVisibility = {
  temperature: boolean
  dewPoint: boolean
  precipitation: boolean
}

type Props = {
  /** 48 コマの実績気象系列 (欠損コマは各値 null)。 */
  series: WeatherActualKoma[]
  visibility: WeatherSeriesVisibility
  /** 気象データが取得できていないか (= 「気象データなし」表示)。 */
  unavailable: boolean
  /** GC コマ番号 (0-origin。DetailChart と揃える)。GC 縦線・GC 後背景に使う。 */
  gcKoma: number
  /**
   * GC 縦線 / 現在時刻線 / GC 以降の網掛けを描くか。 既定 true。
   *
   * 取得失敗の空状態 (AC-BDB-033) で false にする。 空状態の器は `gcKoma: 0` を
   * 持つが**これは実値ではない**ので、 そのまま描くと「GC 00:00」 が確定値の顔で
   * 出る (2026-09-09 レビュー指摘。 SummaryBar 側は `gcKoma: null` で塞いだが
   * チャート 2 つが素通しだった)。
   */
  showGcMarkers?: boolean
  /** 現在コマ番号 (1-origin。DetailChart と揃える)。現在縦線に使う。 */
  currentKoma: number
  /** 表示中の日付。当日以外は現在時刻縦線・GC 縦線・GC 以降網掛け・
   *  ChartMarkerLabels をいずれも描画しない (spec AC-BDB-022a、DetailChart と同じ)。 */
  displayDate: Date
}

// DetailChart と同じ plot 幅の実寸 (X 軸整列・GC/現在 縦線の位置を上下で揃える)。
// margin.left は DetailChart と必ず同値にする (負値は上チャートの MWh 目盛りを
// 見切れさせるため 0)。
const Y_AXIS_WIDTH = 52
const CHART_MARGIN_LEFT = 0
const CHART_MARGIN_RIGHT = 2
// 右軸 (降水量 mm) の予約幅。DetailChart 側も同値で右軸を予約し (目盛り非表示)、
// 上下チャートのプロット右端を一致させる。
const RIGHT_AXIS_WIDTH = 40

/** ℃/mm 値を 0.1 精度で表示 (backend は気温・露点を 0.1 丸め済み)。 */
function fmt1(v: number): string {
  return v.toFixed(1)
}

/** 色付きバッジ (系列ラベル)。BgChart の Badge に倣う。 */
const Badge = ({
  color,
  label,
}: {
  color: string
  label: string
}): React.ReactElement => {
  // 文字色は design (readableOn) と同じ白/黒判定。 それが AA 4.5:1 に届かない系列
  // (露点 = brand-teal 等) だけ地色を同色相のまま沈める (badgeColors 参照)。
  const badge = badgeColors(color)

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 5px',
        // eslint-disable-next-line no-restricted-syntax -- BgChart の tooltip badge と同系統の raw style
        borderRadius: '3px',
        background: badge.background,
        color: badge.color,
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: '16px',
      }}
    >
      {label}
    </span>
  )
}

/**
 * 気象チャートのホバーツールチップ (design/js/charts.js renderBgTempDewChart の
 * tdMouseMove 相当)。コマ番号 + 時間レンジ + GC/現在 バッジ +
 * 気温/予想気温/露点/降水量 バッジ。系列トグル (visibility) が OFF の値は出さない。
 * 予想露点は予想湿度が取れないため対象外。
 *
 * hover 起点なので happy-dom からは開けない。 中身の文言 (とくにコマ 48 の
 * 時間レンジ) を固定するために **単体で描けるよう export する**
 * (発電の `BgTooltipContent` と同じ扱い)。
 */
export const WeatherTooltipContent = ({
  active,
  payload,
  visibility,
  gcTime,
  currentTime,
  isToday,
  t,
}: TooltipContentProps<number, string> & {
  visibility: WeatherSeriesVisibility
  gcTime: string
  currentTime: string
  isToday: boolean
  t: TFunction
}): React.ReactElement | null => {
  const typedPayload = payload as
    | ReadonlyArray<Payload<number, string>>
    | undefined
  if (!active || !typedPayload?.length) return null

  const row = (typedPayload[0] as { payload?: WeatherActualKoma })
    .payload as WeatherActualKoma
  const komaIndex = row.koma + 1
  // コマ 48 の終わりは **24:00**。 `komaEndTime` の godoc:「コマ 48 は "24:00" で
  // 止め、"00:00" へ折り返さない。 計画・実績・インバランスはすべて受渡日単位なので、
  // 翌日へ回ると**別の受渡日のコマ**に読める」。 ここには `% 24` で 0 時へ折り返す
  // ローカル実装があり、 コマ 48 が「23:30 – 00:00」 になっていた (#7064)。
  // 同型のローカル実装は発電の `BgChart` にもあり、 そちらは先に canonical へ寄せた。
  const timeRange = `${row.time} – ${komaEndTime(row.time)}`
  // 非当日を isToday で弾いてから時刻照合する。gcKoma / currentKoma は「該当なし」
  // を 0 で表す (backend の option.None → proto zero value) ため、時刻文字列だけで
  // 突き合わせると 0 が "00:00" に化けて未来日のコマ 1 に GC / 現在バッジが付く
  // (DemandTooltip がコマ番号で判定しているのと同じ罠。こちらは行数の少ない
  // isToday ガードで揃える)。縦線・網掛け・マーカーラベルと出し分けを一致させる。
  const isGc = isToday && row.time === gcTime
  const isNow = isToday && row.time === currentTime

  const badges: React.ReactElement[] = []
  if (visibility.temperature && row.temperature !== null) {
    badges.push(
      <WeatherValueBadge
        key="temp"
        color={BALANCE_WEATHER_TEMP_COLOR}
        label={t('page:balanceDemandDetail.weatherChart.tooltip.temperature')}
        value={`${fmt1(row.temperature)}℃`}
      />,
    )
  }
  if (visibility.temperature && row.temperatureForecast !== null) {
    badges.push(
      <WeatherValueBadge
        key="tempForecast"
        color={BALANCE_WEATHER_TEMP_COLOR}
        label={t(
          'page:balanceDemandDetail.weatherChart.tooltip.temperatureForecast',
        )}
        value={`${fmt1(row.temperatureForecast)}℃`}
      />,
    )
  }
  if (visibility.dewPoint && row.dewPoint !== null) {
    badges.push(
      <WeatherValueBadge
        key="dew"
        color={BALANCE_WEATHER_DEW_COLOR}
        label={t('page:balanceDemandDetail.weatherChart.tooltip.dewPoint')}
        value={`${fmt1(row.dewPoint)}℃`}
      />,
    )
  }
  if (visibility.precipitation && row.precipitation !== null) {
    badges.push(
      <WeatherValueBadge
        key="precip"
        color={BALANCE_WEATHER_PRECIP_COLOR}
        label={t('page:balanceDemandDetail.weatherChart.tooltip.precipitation')}
        value={`${fmt1(row.precipitation)}mm`}
      />,
    )
  }

  return (
    <Box
      minWidth="160px"
      padding="8px 12px"
      background="Surface.primary"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      /* 影は DS の `shadLg`。 生の rgba 直書きは design の `--shadow-lg` とも
         DS とも別物で、 3 チャートが同じ値を各自で持っていた (#7064)。 */
      boxShadow="shadLg"
    >
      <Flex
        alignItems="baseline"
        justifyContent="space-between"
        gap="16px"
        marginBottom="6px"
      >
        <Flex alignItems="center" gap="4px">
          {/* fontSize (14px) + fontWeight (700) の組が役割と一致しないため raw 維持。 */}
          <span style={{ fontSize: '14px', fontWeight: 700 }}>
            {t('page:balanceDemandDetail.weatherChart.tooltip.koma')}{' '}
            {komaIndex}
          </span>
          {isNow && (
            <Badge
              color={LightModeSemanticColors.Surface.accentPrimary.value}
              label={t('page:balanceDemandDetail.weatherChart.tooltip.now')}
            />
          )}
          {isGc && (
            <Badge
              /* GC の赤は **`Surface.negativeHigh`** (#d80004)。 `Text.negativeHigh` (#870103) は
                 暗いマルーンで、 design の GC 色 (`js/charts.js:876` `cssVar('surface-negative-high')`)
                 と別物 (2026-09-03 デザイナー指摘 #7064)。 線・ラベル・バッジで同じ 1 色を使う。 */
              color={LightModeSemanticColors.Surface.negativeHigh.value}
              label={t('page:balanceDemandDetail.weatherChart.tooltip.gc')}
            />
          )}
        </Flex>
        <Text as="span" color="Text.midEmphasis" fontSize="xs">
          {timeRange}
        </Text>
      </Flex>
      <Flex alignItems="center" flexWrap="wrap" gap="8px">
        {badges.length > 0 ? (
          badges
        ) : (
          <Text as="span" color="Text.lowEmphasis" fontSize="xs">
            {t('page:balanceDemandDetail.weatherChart.tooltip.empty')}
          </Text>
        )}
      </Flex>
    </Box>
  )
}

/** ラベル(色付きバッジ) + 値 の 1 組。 */
const WeatherValueBadge = ({
  color,
  label,
  value,
}: {
  color: string
  label: string
  value: string
}): React.ReactElement => {
  return (
    <Box as="span" alignItems="center" gap="3px" display="inline-flex">
      <Badge color={color} label={label} />
      {/* fontSize (14px) + fontWeight (700) の組が役割と一致しないため raw 維持。 */}
      <span
        style={{
          fontSize: '14px',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </Box>
  )
}

/**
 * 同時同量 BG 詳細の気象チャート (design/balance/bg-region.html の
 * chart-tempdew-card)。実績気象 gRPC (WeatherActualService) の 30 分コマ集約を
 * 気温 (実線・左軸 ℃) / 予想気温 (点線・左軸 ℃、予報 gRPC 由来) / 露点 (実線・
 * 左軸 ℃) / 降水量 (棒・右軸 mm) で重ね、GC 縦線・GC 後背景・現在縦線を
 * DetailChart と同じ流儀で描く。ホバーで気温系ツールチップを出す。
 *
 * doji-bg AC-DBD (気象チャート非表示条件): 気象系列トグルが全 OFF の場合は
 * 呼び出し側でそもそも描画しない。取得失敗時は「気象データなし」を表示し、
 * 需要チャート・テーブルは正常表示する (AC-372)。
 */
export const WeatherChart = ({
  series,
  visibility,
  unavailable,
  gcKoma,
  showGcMarkers = true,
  currentKoma,
  displayDate,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // DetailChart と同じ換算 (= 上下チャートで GC/現在 縦線の位置を一致させる)。
  const gcTime = useMemo(() => komaNumberToTime(gcKoma + 1), [gcKoma])
  const currentTime = useMemo(
    () => komaNumberToTime(Math.max(currentKoma, 1)),
    [currentKoma],
  )
  // DetailChart と同じ判定・同じ理由 (spec AC-BDB-022a): displayDate が JST の
  // 当日でなければ現在時刻縦線・GC 縦線・GC 以降網掛け・ChartMarkerLabels を
  // 描画しない。gcKoma の値から逆算せず displayDate を直接判定する — backend
  // `GcKomaForDate`(koma_number.go) は未来日でも JST 23:00 以降は
  // ComputeLockedSlots(gate_close.go) が翌日コマをロック済みとして返すため
  // gcKoma は 0 のままとは限らない (例: 23:10 なら翌日コマ1 がロック済みで
  // gcKoma=1)。gcKoma の値から「0 なら未来日」と推測する実装は実際に壊れる。
  const isTodayDisplay = isTargetDateTodayInJst(toProtoDate(displayDate))
  /** GC 系マーカーを描くか (= 当日表示 かつ 実データあり)。 */
  const showGc = isTodayDisplay && showGcMarkers

  // 気温/露点の左軸ドメイン: 表示中の系列 (気温・予想気温・露点) の実値レンジから
  // 上に約 20%・下に約 10% の余白を確保し、線が天井/床に張り付かないようにする。
  // 気温は 0 始まりでないので固定 [0, max*1.2] ではなくレンジ基準で余白を取る。
  const tempDomain = useMemo((): [number, number] => {
    const values: number[] = []
    for (const k of series) {
      if (visibility.temperature) {
        if (k.temperature !== null) values.push(k.temperature)
        if (k.temperatureForecast !== null) values.push(k.temperatureForecast)
      }
      if (visibility.dewPoint && k.dewPoint !== null) {
        values.push(k.dewPoint)
      }
    }
    if (values.length === 0) {
      return [0, 40]
    }
    const min = Math.min(...values)
    const max = Math.max(...values)
    const span = Math.max(max - min, 1)
    return [Math.floor(min - span * 0.1), Math.ceil(max + span * 0.2)]
  }, [series, visibility])

  const renderTooltipContent = useCallback(
    (props: TooltipContentProps) => (
      <WeatherTooltipContent
        {...(props as TooltipContentProps<number, string>)}
        visibility={visibility}
        currentTime={currentTime}
        gcTime={gcTime}
        isToday={showGc}
        t={t}
      />
    ),
    [currentTime, gcTime, showGc, t, visibility],
  )

  const axisTickStyle = {
    fontSize: 11,
    fill: LightModeSemanticColors.Text.midEmphasis.value,
  }

  return (
    <Flex
      flexDirection="column"
      // overflow は visible: ホバーツールチップが背高でカード外へ出るため
      // (hidden だと下端が見切れる)。
      overflow="visible"
      // カード高の床 (design balance.css `.region-right > .chart-card-tempdew`)。
      // 需要チャート側と同じく、縦が足りない時は潰さず親ペインのスクロールへ逃がす。
      minHeight="220px"
      // design `css/balance.css:396` の `.chart-card-tempdew { margin-top: var(--space-2) }`
      // = 8px。 実装は 12px で 1 段広かった。
      marginTop="8px"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="md"
      backgroundColor="Surface.primary"
    >
      {/* 見出し帯は design `css/balance.css:367` の `.chart-card-title`
          (padding 8px 12px / md・700 / 地 surface-secondary / 下罫線 border-low)。
          design は軸ラベル (`.chart-axis-labels`) を見出しの下の別行に置くが、
          実装は 1 行に同居させている (既存の構造。 ここでは変えない)。 */}
      <Flex
        alignItems="baseline"
        justifyContent="space-between"
        padding="8px 12px"
        borderBottomWidth="1px"
        borderBottomColor="Border.lowEmphasis"
        borderTopRadius="md"
        backgroundColor="Surface.secondary"
      >
        <Text textStyle="chartCardTitle" color="Text.highEmphasis">
          {t('page:balanceDemandDetail.weatherChart.title')}
        </Text>
        <Text
          as="span"
          style={{
            fontSize: fontSizes.xs.value,
            color: LightModeSemanticColors.Text.midEmphasis.value,
          }}
        >
          {t('page:balanceDemandDetail.weatherChart.axisHint')}
        </Text>
      </Flex>

      {unavailable ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          height="160px"
          color="Text.midEmphasis"
        >
          <Text>{t('page:balanceDemandDetail.weatherChart.noData')}</Text>
        </Flex>
      ) : (
        <>
          {/* 現在/GC マーカー: プロット直上の通常フロー行 (DetailChart と同一計算で
              右揃え = 上下チャートで必ず位置が揃い、軸目盛りとも重ならない)。
              非当日 (過去日・未来日) は描画しない (spec AC-BDB-022a)。 */}
          {showGc && (
            <ChartMarkerLabels
              markers={[
                {
                  time: currentTime,
                  text: t('page:balanceDemandDetail.weatherChart.tooltip.now'),
                  color: LightModeSemanticColors.Surface.accentPrimary.value,
                },
                {
                  time: gcTime,
                  text: `${t('page:balanceDemandDetail.weatherChart.tooltip.gc')} ${gcTime}`,
                  color: LightModeSemanticColors.Surface.negativeHigh.value,
                },
              ]}
              rightOffset={RIGHT_AXIS_WIDTH + CHART_MARGIN_RIGHT}
            />
          )}
          {/*
           * css で recharts の目盛テキストを 11px に固定する: Chakra preflight の
           * `"*": { font: "inherit" }` が全要素に効き、Recharts の
           * `tick={{ fontSize }}` (= SVG の presentation 属性 `font-size`) が body
           * 継承値 (14px) に負けて効かない (Supply48DataChart.tsx と同じ罠。値の
           * 出所は `axisTickStyle` / `LeftAlignedYAxisTick` のままにし、効かせ方
           * だけ css 経由に変える)。この画面は全軸 (X軸・左/温度軸・右/降水量軸)
           * 11px で揃っているため selector は 1 本でよい。
           */}
          <Box
            height="200px"
            padding="4px 0"
            css={{ '& .recharts-text': { fontSize: '11px' } }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                margin={{
                  top: 8,
                  right: CHART_MARGIN_RIGHT,
                  left: CHART_MARGIN_LEFT,
                  bottom: 2,
                }}
                data={series}
              >
                <CartesianGrid
                  stroke={LightModeSemanticColors.Border.lowEmphasis.value}
                />
                <XAxis
                  dataKey="time"
                  interval={0}
                  // 需要チャートと同じ point スケールにする (既定では降水量 Bar により
                  // band になり、GC/現在 縦線が需要チャートと半コマずれるため)。
                  scale="point"
                  tick={axisTickStyle}
                  tickFormatter={formatChartTick}
                />
                <YAxis
                  width={Y_AXIS_WIDTH}
                  domain={tempDomain}
                  tick={<LeftAlignedYAxisTick />}
                  yAxisId="temp"
                />
                <YAxis
                  width={RIGHT_AXIS_WIDTH}
                  orientation="right"
                  tick={axisTickStyle}
                  tickFormatter={(v: number) => `${v}`}
                  yAxisId="precip"
                />
                <Tooltip
                  cursor={{ stroke: 'rgba(0,0,0,0.18)', strokeWidth: 1 }}
                  allowEscapeViewBox={{ x: true, y: true }}
                  content={renderTooltipContent}
                  isAnimationActive={false}
                  wrapperStyle={{ zIndex: 30 }}
                />

                {visibility.precipitation && (
                  <Bar
                    fill={BALANCE_WEATHER_PRECIP_COLOR}
                    dataKey="precipitation"
                    fillOpacity={0.5}
                    isAnimationActive={false}
                    yAxisId="precip"
                  />
                )}
                {visibility.temperature && (
                  <Line
                    stroke={BALANCE_WEATHER_TEMP_COLOR}
                    connectNulls
                    dataKey="temperature"
                    dot={false}
                    isAnimationActive={false}
                    strokeWidth={2}
                    type="linear"
                    yAxisId="temp"
                  />
                )}
                {visibility.temperature && (
                  // 予想気温 (予報 gRPC 由来) は赤の点線。値は正時の点のコマ
                  // だけにあり、点どうしを直線で結ぶため connectNulls を付ける
                  // (AC-WD-003a)。実績 (実線) と同色・破線で予報を区別する
                  // (design renderBgTempDewChart: strokeDasharray '4 3')。
                  <Line
                    stroke={BALANCE_WEATHER_TEMP_COLOR}
                    connectNulls
                    dataKey="temperatureForecast"
                    dot={false}
                    isAnimationActive={false}
                    strokeDasharray="4 3"
                    strokeWidth={2}
                    type="linear"
                    yAxisId="temp"
                  />
                )}
                {visibility.dewPoint && (
                  // doji-bg NOTE(露点の算出): 気温または湿度が欠損するコマは露点も
                  // 欠損として線を途切れさせる (= connectNulls を付けない)。
                  <Line
                    stroke={BALANCE_WEATHER_DEW_COLOR}
                    dataKey="dewPoint"
                    dot={false}
                    isAnimationActive={false}
                    strokeWidth={1.5}
                    type="linear"
                    yAxisId="temp"
                  />
                )}

                {/* Reference lines/area (DetailChart と同じ流儀・同色でプロット整列)。
                  ラベルは線に付けず ChartMarkerLabels で上端右にまとめて描く
                  (現在/GC が近接すると個別ラベルが重なるため)。非当日 (過去日・
                  未来日) は描画しない (spec AC-BDB-022a)。 */}
                {showGc && (
                  <>
                    <ReferenceLine
                      stroke={
                        LightModeSemanticColors.Surface.accentPrimary.value
                      }
                      strokeWidth={1.5}
                      x={currentTime}
                      yAxisId="temp"
                    />
                    <ReferenceArea
                      fill={LightModeSemanticColors.Text.disabled.value}
                      stroke="none"
                      fillOpacity={0.08}
                      x1={gcTime}
                      x2="23:30"
                      yAxisId="temp"
                    />
                    <ReferenceLine
                      stroke={
                        LightModeSemanticColors.Surface.negativeHigh.value
                      }
                      strokeDasharray="4 4"
                      strokeWidth={2}
                      x={gcTime}
                      yAxisId="temp"
                    />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Flex>
  )
}
